import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Store,
  Truck,
  MapPin,
  Clock,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Navigation
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCart } from '@/context/CartContext';
import { businessInfo } from '@/data/menuData';

const DELIVERY_QUOTE_URL = 'https://www.sapori1989.it/menu-digitale/api/delivery/quote.php';

// Tariffe consegna DEFINITIVE
const DELIVERY_TIERS = [
  { minKm: 0, maxKm: 2, fee: 3.0, label: '1-2 km' },
  { minKm: 2, maxKm: 10, fee: 5.0, label: '2-10 km' },
  { minKm: 10, maxKm: 20, fee: 9.0, label: '10-20 km' },
  { minKm: 20, maxKm: 30, fee: 15.0, label: '20-30 km' },
  { minKm: 30, maxKm: 50, fee: 19.0, label: '30-50 km' }
];

const getDeliveryTier = (distanceKm) => {
  if (distanceKm === null || distanceKm === undefined || Number.isNaN(distanceKm)) return null;
  if (distanceKm <= 0) return null;
  if (distanceKm > 50) return { fee: null, label: '> 50 km', unavailable: true };

  for (const tier of DELIVERY_TIERS) {
    if (distanceKm > tier.minKm && distanceKm <= tier.maxKm) {
      return { fee: tier.fee, label: tier.label, unavailable: false };
    }
  }

  if (distanceKm <= 2) {
    return { fee: 3.0, label: '1-2 km', unavailable: false };
  }

  return { fee: null, label: '> 50 km', unavailable: true };
};

const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour <= 20; hour++) {
    for (let min = 0; min < 60; min += 15) {
      if (hour === 20 && min > 30) break;
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      slots.push(time);
    }
  }
  return slots;
};

const generateAvailableDates = () => {
  const dates = [];
  const today = new Date();

  for (let i = 0; i < 14; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);

    if (date.getDay() === 0) continue;

    dates.push({
      value: date.toISOString().split('T')[0],
      label: date.toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })
    });

    if (dates.length >= 7) break;
  }

  return dates;
};

// Loader Google Maps singleton
let googleMapsScriptPromise = null;

const loadGoogleMapsPlacesScript = () => {
  if (window.google?.maps?.places) {
    return Promise.resolve(true);
  }

  if (googleMapsScriptPromise) {
    return googleMapsScriptPromise;
  }

  googleMapsScriptPromise = new Promise((resolve, reject) => {
    const apiKey =
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      window.REACT_APP_GOOGLE_MAPS_API_KEY ||
      '';

    if (!apiKey) {
      reject(new Error('Google Maps API key mancante'));
      return;
    }

    const existingScript = document.querySelector('script[data-google-maps-places="true"]');

    if (existingScript) {
      let attempts = 0;
      const maxAttempts = 100;

      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(interval);
          resolve(true);
          return;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          reject(new Error('Google Maps Places non disponibile'));
        }
      }, 100);

      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=it&region=it`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsPlaces = 'true';

    script.onload = () => {
      let attempts = 0;
      const maxAttempts = 50;

      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(interval);
          resolve(true);
          return;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          reject(new Error('Google Maps Places non disponibile dopo il load'));
        }
      }, 100);
    };

    script.onerror = () => {
      reject(new Error('Errore caricamento script Google Maps'));
    };

    document.head.appendChild(script);
  });

  return googleMapsScriptPromise;
};

// Hook robusto autocomplete
const useGooglePlacesAutocomplete = ({ inputElement, enabled, onPlaceSelect }) => {
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(!!window.google?.maps?.places);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!enabled) return;

    let mounted = true;

    loadGoogleMapsPlacesScript()
      .then(() => {
        if (!mounted) return;
        setIsLoaded(true);
        setLoadError('');
      })
      .catch((error) => {
        console.error('Google Maps load error:', error);
        if (!mounted) return;
        setLoadError(error.message || 'Errore caricamento Google Maps');
        setIsLoaded(false);
      });

    return () => {
      mounted = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !isLoaded || !inputElement || !window.google?.maps?.places) return;

    try {
      if (listenerRef.current) {
        window.google.maps.event.removeListener(listenerRef.current);
        listenerRef.current = null;
      }

      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputElement, {
        componentRestrictions: { country: 'it' },
        types: ['address'],
        fields: ['place_id', 'formatted_address', 'geometry', 'name']
      });

      listenerRef.current = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace?.();

        if (place && place.place_id) {
          onPlaceSelect({
            placeId: place.place_id,
            formattedAddress: place.formatted_address || place.name || '',
            geometry: place.geometry || null
          });
        }
      });
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      setLoadError('Errore inizializzazione autocomplete Google');
    }

    return () => {
      if (listenerRef.current && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listenerRef.current);
        listenerRef.current = null;
      }
      autocompleteRef.current = null;
    };
  }, [enabled, inputElement, isLoaded, onPlaceSelect]);

  return { isLoaded, loadError };
};

export const FulfillmentSelector = () => {
  const {
    fulfillmentType,
    setFulfillmentType,
    deliveryAddress,
    setDeliveryAddress,
    deliveryFee,
    setDeliveryFee,
    deliveryDistance,
    setDeliveryDistance,
    deliveryPlaceId,
    setDeliveryPlaceId,
    pickupDate,
    setPickupDate,
    pickupTime,
    setPickupTime,
    setDeliveryUnavailable
  } = useCart();

  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [deliveryTier, setDeliveryTier] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [inputValue, setInputValue] = useState(deliveryAddress || '');
  const [deliveryInputEl, setDeliveryInputEl] = useState(null);

  const timeSlots = generateTimeSlots();
  const availableDates = generateAvailableDates();

  const handlePlaceSelect = useCallback(
    (place) => {
      setSelectedPlace(place);
      setInputValue(place.formattedAddress || '');
      setDeliveryAddress(place.formattedAddress || '');
      setAddressError('');

      setDeliveryDistance(null);
      setDeliveryFee(0);
      setDeliveryTier(null);

      if (setDeliveryPlaceId) {
        setDeliveryPlaceId(place.placeId || null);
      }

      if (setDeliveryUnavailable) {
        setDeliveryUnavailable(false);
      }
    },
    [setDeliveryAddress, setDeliveryDistance, setDeliveryFee, setDeliveryPlaceId, setDeliveryUnavailable]
  );

  const { isLoaded: googleLoaded, loadError: googleLoadError } = useGooglePlacesAutocomplete({
    inputElement: deliveryInputEl,
    enabled: fulfillmentType === 'DELIVERY',
    onPlaceSelect: handlePlaceSelect
  });

  useEffect(() => {
    setInputValue(deliveryAddress || '');
  }, [deliveryAddress]);

  useEffect(() => {
    if (fulfillmentType !== 'DELIVERY') {
      setAddressError('');
    }
  }, [fulfillmentType]);

  const calculateDistance = async () => {
    if (!selectedPlace || !selectedPlace.placeId) {
      setAddressError('Seleziona un indirizzo dai suggerimenti');
      return;
    }

    setIsCalculatingDistance(true);
    setAddressError('');

    try {
      const response = await fetch(DELIVERY_QUOTE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          destination_place_id: selectedPlace.placeId,
          destination_label: selectedPlace.formattedAddress
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error || 'Errore nel calcolo della distanza');
      }

      const backendDistance = Number(data.distance_km);
      const normalizedTier = getDeliveryTier(backendDistance);

      if (!normalizedTier) {
        throw new Error('Impossibile determinare la fascia di consegna');
      }

      setDeliveryDistance(backendDistance);

      if (setDeliveryPlaceId) {
        setDeliveryPlaceId(selectedPlace.placeId);
      }

      if (normalizedTier.unavailable) {
        setAddressError('Spiacenti, la consegna non è disponibile per questa zona (oltre 50 km)');
        setDeliveryFee(0);
        setDeliveryTier(normalizedTier);
        if (setDeliveryUnavailable) setDeliveryUnavailable(true);
      } else {
        setDeliveryFee(normalizedTier.fee);
        setDeliveryTier(normalizedTier);
        if (setDeliveryUnavailable) setDeliveryUnavailable(false);
      }
    } catch (error) {
      console.error('Distance calculation error:', error);
      setAddressError(error.message || 'Errore nel calcolo della distanza. Riprova.');
      setDeliveryFee(0);
      setDeliveryTier(null);
      if (setDeliveryUnavailable) setDeliveryUnavailable(true);
    } finally {
      setIsCalculatingDistance(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setDeliveryAddress(value);
    setAddressError('');

    if (selectedPlace && value !== selectedPlace.formattedAddress) {
      setSelectedPlace(null);
      setDeliveryDistance(null);
      setDeliveryFee(0);
      setDeliveryTier(null);
      if (setDeliveryPlaceId) setDeliveryPlaceId(null);
      if (setDeliveryUnavailable) setDeliveryUnavailable(false);
    }
  };

  return (
    <section className="py-10 px-4 bg-muted/30 scroll-mt-24" id="modalita-ordine">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Come vuoi ricevere il tuo <span className="gradient-instagram-text">ordine</span>?
          </h2>
          <p className="text-sm text-muted-foreground">
            Scegli tra ritiro in sede o consegna a domicilio
          </p>
        </div>

        <RadioGroup value={fulfillmentType} onValueChange={setFulfillmentType} className="space-y-4">
          <Card
            className={`p-4 cursor-pointer transition-all ${
              fulfillmentType === 'PICKUP'
                ? 'border-2 border-instagram-purple shadow-instagram'
                : 'border border-border hover:border-instagram-purple/30'
            }`}
            onClick={() => setFulfillmentType('PICKUP')}
          >
            <div className="flex items-start gap-4">
              <RadioGroupItem value="PICKUP" id="pickup" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg gradient-instagram">
                    <Store className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <Label htmlFor="pickup" className="text-lg font-semibold text-foreground cursor-pointer">
                      Ritiro in Sede
                    </Label>
                    <p className="text-sm text-success font-medium">Gratuito</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <MapPin className="w-4 h-4" />
                  <span>{businessInfo.address}</span>
                </div>

                {fulfillmentType === 'PICKUP' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-border animate-slide-up">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-instagram-purple" />
                        Data ritiro
                      </Label>
                      <Select value={pickupDate} onValueChange={setPickupDate}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona data" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDates.map((date) => (
                            <SelectItem key={date.value} value={date.value}>
                              {date.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-instagram-purple" />
                        Orario ritiro
                      </Label>
                      <Select value={pickupTime} onValueChange={setPickupTime}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona orario" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="sm:col-span-2">
                      <Alert className="bg-instagram-purple/10 border-instagram-purple/30">
                        <Clock className="w-4 h-4 text-instagram-purple" />
                        <AlertDescription className="text-sm text-foreground">
                          Orario continuato: <strong>09:00 - 20:30</strong> (Lun-Sab)
                        </AlertDescription>
                      </Alert>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card
            className={`p-4 cursor-pointer transition-all ${
              fulfillmentType === 'DELIVERY'
                ? 'border-2 border-success shadow-medium'
                : 'border border-border hover:border-success/30'
            }`}
            onClick={() => setFulfillmentType('DELIVERY')}
          >
            <div className="flex items-start gap-4">
              <RadioGroupItem value="DELIVERY" id="delivery" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-success">
                    <Truck className="w-5 h-5 text-success-foreground" />
                  </div>
                  <div>
                    <Label htmlFor="delivery" className="text-lg font-semibold text-foreground cursor-pointer">
                      Consegna a Domicilio
                    </Label>
                    <p className="text-sm text-muted-foreground">A partire da €3.00</p>
                  </div>
                </div>

                {fulfillmentType === 'DELIVERY' && (
                  <div className="space-y-4 mt-4 pt-4 border-t border-border animate-slide-up">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-success" />
                        Indirizzo di consegna
                      </Label>

                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            ref={setDeliveryInputEl}
                            placeholder="Cerca città, via e numero civico..."
                            value={inputValue}
                            onChange={handleInputChange}
                            className={`pr-10 ${selectedPlace ? 'border-success' : ''}`}
                          />
                          {selectedPlace && (
                            <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-success" />
                          )}
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          onClick={calculateDistance}
                          disabled={isCalculatingDistance || !selectedPlace}
                          className="border-success text-success hover:bg-success/10 min-w-[100px]"
                        >
                          {isCalculatingDistance ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Navigation className="w-4 h-4 mr-1" />
                              Calcola
                            </>
                          )}
                        </Button>
                      </div>

                      {!googleLoaded && !googleLoadError && (
                        <p className="text-xs text-muted-foreground">
                          Caricamento suggerimenti Google Maps...
                        </p>
                      )}

                      {googleLoadError && (
                        <p className="text-xs text-destructive">
                          {googleLoadError}
                        </p>
                      )}

                      {!selectedPlace && inputValue && googleLoaded && (
                        <p className="text-xs text-warning">
                          Seleziona un indirizzo dai suggerimenti di Google
                        </p>
                      )}
                    </div>

                    {addressError && (
                      <Alert variant="destructive">
                        <AlertCircle className="w-4 h-4" />
                        <AlertDescription>{addressError}</AlertDescription>
                      </Alert>
                    )}

                    {deliveryDistance !== null && !addressError && deliveryTier && !deliveryTier.unavailable && (
                      <Alert className="bg-success/10 border-success/30">
                        <CheckCircle2 className="w-4 h-4 text-success" />
                        <AlertDescription className="text-foreground">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                            <span>
                              <strong>Distanza:</strong> {deliveryDistance.toFixed(1)} km
                            </span>
                            <span className="text-muted-foreground">•</span>
                            <span>
                              <strong>Fascia:</strong> {deliveryTier.label}
                            </span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-success font-bold">
                              Consegna: €{deliveryFee.toFixed(2)}
                            </span>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        Tariffe consegna (modalità auto):
                      </p>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                        <div className={`flex justify-between p-1.5 rounded ${deliveryTier?.label === '1-2 km' ? 'bg-success/20' : ''}`}>
                          <span>1–2 km</span>
                          <span className="font-medium">€3.00</span>
                        </div>

                        <div className={`flex justify-between p-1.5 rounded ${deliveryTier?.label === '2-10 km' ? 'bg-success/20' : ''}`}>
                          <span>2–10 km</span>
                          <span className="font-medium">€5.00</span>
                        </div>

                        <div className={`flex justify-between p-1.5 rounded ${deliveryTier?.label === '10-20 km' ? 'bg-success/20' : ''}`}>
                          <span>10–20 km</span>
                          <span className="font-medium">€9.00</span>
                        </div>

                        <div className={`flex justify-between p-1.5 rounded ${deliveryTier?.label === '20-30 km' ? 'bg-success/20' : ''}`}>
                          <span>20–30 km</span>
                          <span className="font-medium">€15.00</span>
                        </div>

                        <div className={`flex justify-between p-1.5 rounded ${deliveryTier?.label === '30-50 km' ? 'bg-success/20' : ''}`}>
                          <span>30–50 km</span>
                          <span className="font-medium">€19.00</span>
                        </div>

                        <div className={`flex justify-between p-1.5 rounded ${deliveryTier?.unavailable ? 'bg-destructive/20 text-destructive' : ''}`}>
                          <span>&gt; 50 km</span>
                          <span className="font-medium">N/D</span>
                        </div>
                      </div>

                      <p className="text-xs text-destructive mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Oltre 50 km la consegna non è disponibile
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </RadioGroup>
      </div>
    </section>
  );
};
